'use client';

import { createContext, useContext, ReactNode } from 'react';
import { useCertificationComparison, UseCertificationComparisonReturn } from '@/hooks/useCertificationComparison';

interface CertificationComparisonContextValue extends UseCertificationComparisonReturn {
    currentCertificationId: string;
    weightProfileId: string | null;
    hideSelectionControls: boolean;
    primaryCertName?: string;
    canEditColors?: boolean;
}

const CertificationComparisonContext = createContext<CertificationComparisonContextValue | null>(null);

interface CertificationComparisonProviderProps {
    children: ReactNode;
    certificationId: string;
    weightProfileId: string | null;
    comparisonState?: UseCertificationComparisonReturn;
    hideSelectionControls?: boolean;
    primaryCertName?: string;
    canEditColors?: boolean;
    canPersist?: boolean;
}

/**
 * Context provider for certification comparison state.
 * Wrap score preview and report sections with this provider.
 */
export function CertificationComparisonProvider({
    children,
    certificationId,
    weightProfileId,
    comparisonState: providedState,
    hideSelectionControls = false,
    primaryCertName,
    canEditColors = false,
    canPersist = false,
}: CertificationComparisonProviderProps) {
    // Use provided state if available, otherwise create new state
    // When providedState is given, internal hook uses canPersist=false to avoid duplicate localStorage writes
    const ownComparison = useCertificationComparison(certificationId, weightProfileId, providedState ? false : canPersist);
    const comparison = providedState || ownComparison;

    return (
        <CertificationComparisonContext.Provider
            value={{
                ...comparison,
                currentCertificationId: certificationId,
                weightProfileId,
                hideSelectionControls,
                primaryCertName,
                canEditColors,
            }}
        >
            {children}
        </CertificationComparisonContext.Provider>
    );
}

/**
 * Hook to access comparison context.
 * Must be used within a CertificationComparisonProvider.
 */
export function useCertificationComparisonContext(): CertificationComparisonContextValue {
    const context = useContext(CertificationComparisonContext);
    if (!context) {
        throw new Error(
            'useCertificationComparisonContext must be used within a CertificationComparisonProvider'
        );
    }
    return context;
}
